/**
 * Single Post Details — frontend interactions (download PDF, copy link, newsletter preview).
 */
import { initNewsletterForms } from '../shared/newsletter-form/init';
import { downloadPostPdf } from './pdf-download';
import { initPostLightbox } from './lightbox';

( function () {
	'use strict';

	/** @type {HTMLElement[]} */
	let printHiddenNodes = [];

	/** @type {HTMLButtonElement | null} */
	let activeDownloadButton = null;

	/**
	 * Hide single-template sections that sit after the article (related band, newsletter).
	 * Used for manual browser print (Ctrl/Cmd+P).
	 *
	 * @return {HTMLElement[]} Nodes that received .alteia-print-hidden.
	 */
	function hidePostTemplateExtrasForPrint() {
		const hidden = [];
		const seen = new Set();

		const markHidden = ( node ) => {
			if ( ! node || seen.has( node ) ) {
				return;
			}
			seen.add( node );
			node.classList.add( 'alteia-print-hidden' );
			hidden.push( node );
		};

		const articleBlock =
			document.querySelector( '.wp-block-alteia-single-post-details' ) ||
			document.querySelector( '.alteia-single-post' );

		if ( articleBlock ) {
			const articleRegion =
				articleBlock.closest( '.wp-block-group' ) || articleBlock;

			let sibling = articleRegion.nextElementSibling;
			while ( sibling ) {
				if (
					! sibling.matches(
						'header, footer, .wp-block-template-part'
					)
				) {
					markHidden( sibling );
				}
				sibling = sibling.nextElementSibling;
			}
		}

		document
			.querySelectorAll(
				'.alteia-no-print, .alteia-single-post-related-wrap, .alteia-related-posts-band, .wp-block-alteia-related-posts, .wp-block-group.has-eef-4-f-4-background-color, section.alteia-newsletter-section--single-post, div.wp-block-group.has-background[style*="eef4f4"], div.wp-block-group.has-background[style*="EEF4F4"]'
			)
			.forEach( markHidden );

		return hidden;
	}

	function restorePrintHiddenNodes() {
		printHiddenNodes.forEach( ( node ) =>
			node.classList.remove( 'alteia-print-hidden' )
		);
		printHiddenNodes = [];
	}

	/**
	 * Update download button label and busy state.
	 *
	 * @param {HTMLButtonElement}                    button Download button.
	 * @param {'default'|'generating'|'downloading'} state  Button state.
	 */
	function setDownloadButtonState( button, state ) {
		const labelEl = button.querySelector(
			'.alteia-single-post__download-label'
		);

		if ( ! labelEl ) {
			return;
		}

		const labels = {
			default: button.dataset.defaultLabel || 'Download',
			generating: button.dataset.generatingLabel || 'Generating PDF...',
			downloading: button.dataset.downloadingLabel || 'Downloading',
		};

		const isBusy = state !== 'default';

		button.disabled = isBusy;
		button.classList.toggle( 'is-busy', isBusy );
		button.setAttribute( 'aria-busy', isBusy ? 'true' : 'false' );
		labelEl.textContent = labels[ state ] || labels.default;
	}

	/**
	 * Show a temporary failure message on the download button.
	 *
	 * @param {HTMLButtonElement} button Download button.
	 */
	function showDownloadFailed( button ) {
		const labelEl = button.querySelector(
			'.alteia-single-post__download-label'
		);

		if ( ! labelEl ) {
			return;
		}

		labelEl.textContent =
			button.dataset.failedLabel || 'Download failed — try again';

		button.disabled = false;
		button.classList.remove( 'is-busy' );
		button.setAttribute( 'aria-busy', 'false' );

		window.setTimeout( () => {
			setDownloadButtonState( button, 'default' );
		}, 3000 );
	}

	/**
	 * Generate and download the post as a PDF file.
	 *
	 * @param {HTMLButtonElement} button Download button.
	 */
	async function handleDownload( button ) {
		if ( button.disabled ) {
			return;
		}

		const root =
			button.closest( '.alteia-single-post' ) ||
			button.closest( '.wp-block-alteia-single-post-details' );

		if ( ! root ) {
			return;
		}

		activeDownloadButton = button;
		setDownloadButtonState( button, 'generating' );

		const postTitle =
			button.getAttribute( 'data-post-title' ) ||
			button.dataset.postTitle ||
			'';

		try {
			await downloadPostPdf( root, postTitle, ( state ) => {
				if ( activeDownloadButton === button ) {
					setDownloadButtonState( button, state );
				}
			} );

			if ( activeDownloadButton === button ) {
				setDownloadButtonState( button, 'default' );
				activeDownloadButton = null;
			}
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.error( 'PDF download failed', error );

			if ( activeDownloadButton === button ) {
				showDownloadFailed( button );
				activeDownloadButton = null;
			}
		}
	}

	window.addEventListener( 'beforeprint', () => {
		printHiddenNodes = hidePostTemplateExtrasForPrint();
		document.body.classList.add( 'alteia-single-post--printing' );
	} );

	window.addEventListener( 'afterprint', () => {
		restorePrintHiddenNodes();
		document.body.classList.remove( 'alteia-single-post--printing' );
	} );

	/**
	 * Copy URL to clipboard and show tooltip feedback.
	 *
	 * @param {HTMLButtonElement} button Copy button element.
	 */
	async function handleCopyLink( button ) {
		const url = button.getAttribute( 'data-copy-url' );

		if ( ! url ) {
			return;
		}

		const tooltip = button.querySelector(
			'.alteia-single-post__share-tooltip'
		);

		try {
			if ( navigator.clipboard?.writeText ) {
				await navigator.clipboard.writeText( url );
			} else {
				const textarea = document.createElement( 'textarea' );
				textarea.value = url;
				textarea.setAttribute( 'readonly', '' );
				textarea.style.position = 'absolute';
				textarea.style.left = '-9999px';
				document.body.appendChild( textarea );
				textarea.select();
				document.execCommand( 'copy' );
				document.body.removeChild( textarea );
			}

			button.classList.add( 'is-copied' );

			if ( tooltip ) {
				tooltip.hidden = false;
				tooltip.textContent =
					button.dataset.copiedLabel || 'Link copied!';
			}

			window.setTimeout( () => {
				button.classList.remove( 'is-copied' );

				if ( tooltip ) {
					tooltip.hidden = true;
					tooltip.textContent =
						button.dataset.copyLabel || 'Copy link to clipboard';
				}
			}, 2000 );
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.error( 'Copy link failed', error );
		}
	}

	/**
	 * Initialize block interactions within a root node.
	 *
	 * @param {HTMLElement} root Root element.
	 */
	function initBlock( root ) {
		root.querySelectorAll( '.alteia-single-post__download' ).forEach(
			( button ) => {
				if ( button.dataset.downloadBound === 'true' ) {
					return;
				}

				button.dataset.downloadBound = 'true';

				button.addEventListener( 'click', ( event ) => {
					event.preventDefault();
					handleDownload( button );
				} );
			}
		);

		root.querySelectorAll(
			'.alteia-single-post__share-link--link[data-copy-url]'
		).forEach( ( button ) => {
			button.addEventListener( 'click', ( event ) => {
				event.preventDefault();
				handleCopyLink( button );
			} );
		} );
	}

	function boot() {
		document
			.querySelectorAll( '.alteia-single-post' )
			.forEach( ( root ) => {
				initBlock( root );
				initNewsletterForms( root );
				initPostLightbox( root );
			} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', boot );
	} else {
		boot();
	}
} )();
